import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal } from '@angular/core';
import { ShellStateService } from '../../../../core/services/shell-state.service';
import { BadgeService } from '../../../../core/services/badge.service';
import { ProfileDataService } from '../../../../core/services/profile-data.service';
import { ViewerIdentityService } from '../../../../core/services/viewer-identity.service';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'im-profile-avatar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe],
  template: `
    <button class="avatar-btn" (click)="shell.toggleProfilePanel()"
      [class.active]="shell.rightPanel() === 'profile'"
      [attr.aria-pressed]="shell.rightPanel() === 'profile'"
      [attr.aria-label]="'shell.profile_avatar.profile_and_settings' | transloco">
      @if (picture() && !pictureFailed()) {
        <!-- alt="" on purpose: the button already carries the accessible
             name, so alt text here would double-announce — and when the
             fetch fails it is the alt string the browser paints over the
             avatar. Failure is handled by falling back to initials. -->
        <img [src]="picture()" alt="" class="avatar-img" (error)="onPictureError()" />
      } @else {
        <span class="avatar-circle">{{ initials() }}</span>
      }
      <span class="status-dot" [style.background]="profileData.derivedStatus().colour"></span>
      @if (badge.aggregateBadge().type !== 'none') {
        <span class="badge-dot" [class.urgent]="badge.aggregateBadge().colour === 'red'">
          @if (badge.aggregateBadge().type === 'count') {
            {{ badge.aggregateBadge().count }}
          }
        </span>
      }
    </button>
  `,
  styles: `
    .avatar-btn {
      position: relative;
      width: 36px;
      height: 36px;
      padding: 0;
      background: none;
      border: 2px solid transparent;
      border-radius: 50%;
      cursor: pointer;
      transition: border-color var(--transition);

      &:hover { border-color: var(--border-med); }
      &.active { border-color: var(--color-primary); }
    }

    .avatar-img {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
    }

    .avatar-circle {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: linear-gradient(135deg, #da1e48, #e83e63);
      color: var(--text-on-primary, #fff);
      font-size: 12px;
      font-weight: 600;
      font-family: inherit;
    }

    .status-dot {
      position: absolute;
      bottom: -1px;
      right: -1px;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      border: 2px solid var(--bg);
    }

    .badge-dot {
      position: absolute;
      top: -2px;
      right: -2px;
      min-width: 8px;
      height: 8px;
      border-radius: 8px;
      background: #60a5fa;
      border: 2px solid var(--bg);
      font-size: 0;
      line-height: 0;

      &.urgent {
        background: var(--danger);
        min-width: 16px;
        height: 16px;
        border-radius: 16px;
        font-size: 9px;
        font-weight: 700;
        color: var(--text-on-primary, #fff);
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
      }
    }
  `,
})
export class ProfileAvatarComponent {
  protected readonly shell = inject(ShellStateService);
  protected readonly badge = inject(BadgeService);
  protected readonly profileData = inject(ProfileDataService);
  private readonly identity = inject(ViewerIdentityService);

  protected readonly picture = computed(() => this.profileData.profile()?.picture);

  /**
   * A stored picture URL is not a loadable picture. The profile can carry a
   * URL that 403s or 404s (asset removed, bucket permissions changed); the
   * `@if` above branches on the URL EXISTING, so without this the browser
   * renders the broken-image glyph over the header. Flip on the img's own
   * `error` and fall back to initials — the same auto-fallback `paper-avatar`
   * documents. Reset whenever the URL changes so a later good URL recovers.
   */
  protected readonly pictureFailed = linkedSignal({
    source: this.picture,
    computation: () => false,
  });

  protected onPictureError(): void {
    this.pictureFailed.set(true);
  }

  protected readonly initials = computed(() => {
    const name = this.identity.viewerName();
    if (!name) return '?';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.charAt(0).toUpperCase();
  });
}
